import { useState, useRef, useCallback, useEffect } from 'react';
import { ShieldCheck, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { OtpInput } from './OtpInput';
import { useSearchParams } from 'react-router-dom';
import { organizationInviteAPI } from '@/api/organizationInviteApi';
import { UserInviteLogin } from './UserInviteLogin';
 

type PageState =
  | { stage: 'validating' }                          
  | { stage: 'invalid'; reason: string }            
  | { stage: 'otp'; invitationId: string }          
  | { stage: 'verifying' }  
  | { stage: 'setup'; tempToken: string }                      
  | { stage: 'success' }                           
  | { stage: 'error'; message: string }
const ValidatingSkeleton = () => (
    <div className="flex flex-col items-center gap-3 animate-pulse">
      <div className="w-14 h-14 rounded-2xl bg-gray-100" />
      <div className="w-36 h-5 rounded bg-gray-100" />
      <div className="w-52 h-4 rounded bg-gray-100" />
    </div>
  );
  const OTP_LENGTH    = 6;
  type OtpInputState = 'idle' | 'error';
 
export const OtpVerification = () => {
    const [searchParams]  = useSearchParams();
   
    const token =
      searchParams.get('token') ??
      searchParams.get('invitation_token') ??
      '';
    const email = searchParams.get('email') ?? searchParams.get('e') ?? '';
   
    const [pageState, setPageState] = useState<PageState>({ stage: 'validating' });
    const [digits, setDigits]       = useState<string[]>(Array(OTP_LENGTH).fill(''));
    const [otpError, setOtpError]   = useState('');
   
    const otp      = digits.join('');
    const isFilled = otp.length === OTP_LENGTH;
   
    const initRef = useRef(false);
   
    useEffect(() => {
      if (initRef.current) return;
      initRef.current = true;

      if (!token || !email) {
        setPageState({ stage: 'invalid', reason: 'Invalid or missing invite link.' });
        return;
      }
   
      organizationInviteAPI
        .validateInvite({ email, token })
        .then((res) => {
          if (!res.valid) {
            setPageState({ stage: 'invalid', reason: 'This invite link is invalid or has expired.' });
            return;
          }
          return organizationInviteAPI
            .sendOtp({ email, invitationId: res.invitation_id })
            .then(() => {
              setPageState({ stage: 'otp', invitationId: res.invitation_id });
            });
        })
        .catch((err) => {
          const reason =
            err?.response?.data?.detail ??
            err?.message ??
            'Failed to validate invite. Please try again.';
          setPageState({ stage: 'invalid', reason });
        });
    }, []);
   
    const handleVerify = async () => {
      if (!isFilled || pageState.stage !== 'otp') return;
      const { invitationId } = pageState;
   
      setPageState({ stage: 'verifying' });
      setOtpError('');
   
      try {
        const res = await organizationInviteAPI.verfiyOtp({ email, invitationId, otp });
        if (!res.verified) throw new Error('Invalid code. Please try again.');
        setPageState({ stage: 'setup', tempToken: res.temp_token });
      } catch (err: any) {
        const msg =
          err?.response?.data?.detail ??
          err?.message ??
          'Invalid code. Please try again.';
        setOtpError(msg);
        setDigits(Array(OTP_LENGTH).fill(''));
        setPageState({ stage: 'otp', invitationId });
      }
    };
   
   
    useEffect(() => {
      if (isFilled && pageState.stage === 'otp') handleVerify();
    }, [otp, pageState.stage]);
   
    const handleDigitChange = useCallback((val: string[]) => {
      setDigits(val);
      if (otpError) setOtpError('');
    }, [otpError]);
    const isOtpDisabled = pageState.stage === 'verifying' || pageState.stage === 'success';
    const inputState: OtpInputState = otpError ? 'error' : 'idle';
    const showOtpInput  = pageState.stage === 'otp' || pageState.stage === 'verifying';

    if (pageState.stage === 'setup') {
        return <UserInviteLogin tempToken={pageState.tempToken} />;
      }
   
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          {pageState.stage === 'validating' && (
            <div className="py-4">
              <ValidatingSkeleton />
              <p className="text-center text-sm text-gray-400 mt-6">Validating your invite…</p>
            </div>
          )}
   
          {pageState.stage === 'invalid' && (
            <div className="flex flex-col items-center gap-3 text-center py-4">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Link Invalid</h2>
                <p className="text-sm text-gray-500">{pageState.reason}</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Contact your admin to request a new invite.
              </p>
            </div>
          )}

          {(showOtpInput || pageState.stage === 'success') && (
            <>
              <div className="flex flex-col items-center gap-3 mb-8 text-center">
                <div
                  className={[
                    'w-14 h-14 rounded-2xl flex items-center justify-center transition-colors duration-300',
                    pageState.stage === 'success' ? 'bg-green-50'
                    : otpError                    ? 'bg-red-50'
                    : 'bg-primary-50',
                  ].join(' ')}
                >
                  {pageState.stage === 'success' ? (
                    <CheckCircle2 className="w-7 h-7 text-green-600" />
                  ) : (
                    <ShieldCheck
                      className={[
                        'w-7 h-7 transition-colors duration-300',
                        otpError ? 'text-red-500' : 'text-primary-600',
                      ].join(' ')}
                    />
                  )}
                </div>
   
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-1">
                    {pageState.stage === 'success' ? 'Verified!' : 'Check your email'}
                  </h2>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {pageState.stage === 'success'
                      ? 'Your identity has been confirmed. Setting up your account…'
                      : `We sent a ${OTP_LENGTH}-digit code to your email address.`
                    }
                  </p>
                </div>
              </div>
              {pageState.stage !== 'success' && (
                <div className="space-y-6">
                  <OtpInput
                    value={digits}
                    onChange={handleDigitChange}
                    inputState={inputState}
                    disabled={isOtpDisabled}
                  />
                  <div className="min-h-[20px] text-center">
                    {otpError && (
                      <p className="text-sm text-red-600 font-medium animate-in fade-in slide-in-from-top-1 duration-200">
                        {otpError}
                      </p>
                    )}
                  </div>
   
                  {/* Primary CTA */}
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={!isFilled || isOtpDisabled}
                    className="btn btn-primary w-full flex items-center justify-center gap-2 min-h-[44px]"
                  >
                    {pageState.stage === 'verifying' ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Verifying…
                      </span>
                    ) : (
                      <>
                        Verify Code
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                </div>
              )}
   
              {pageState.stage === 'success' && (
                <div className="flex justify-center animate-in fade-in zoom-in-95 duration-300">
                  <div className="flex items-center gap-2 px-5 py-2.5 bg-green-50 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Email verified</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };
  